import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RentalInfo } from '@/hooks/types/rentalTypes';
import { formatDate } from '@/utils/dateUtils';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Calendar, FileText } from 'lucide-react';
import { RentalRulesDialog } from './RentalRulesDialog';
import { BikeInstructionDialog } from './BikeInstructionDialog';

interface ActiveRentalCardProps {
  rental: RentalInfo | null;
  loading: boolean;
}

export const ActiveRentalCard: React.FC<ActiveRentalCardProps> = ({ rental, loading }) => {
  const [isRulesDialogOpen, setIsRulesDialogOpen] = useState(false);
  const [isInstructionsDialogOpen, setIsInstructionsDialogOpen] = useState(false);

  function openRulesDialog() {
    setIsRulesDialogOpen(true);
  }

  function openInstructionsDialog() {
    setIsInstructionsDialogOpen(true);
  }

  const handleDocumentClick = () => {
    // Используем прямую ссылку из поля documents_url
    if (rental?.documentsUrl) {
      window.open(rental.documentsUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-1/3 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full mb-4" />
          <div className="flex justify-between">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-8 w-24" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!rental) {
    return (
      <Card className="bg-muted/40">
        <CardHeader>
          <CardTitle>Нет активных аренд</CardTitle>
          <CardDescription>У вас нет активных аренд велосипедов</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
          <AlertCircle className="h-12 w-12 mb-4 text-muted-foreground/60" />
          <p>
            На данный момент у вас нет активных аренд. Вы можете арендовать велосипед на странице выбора тарифов.
          </p>
        </CardContent>
      </Card>
    );
  }

  const rentalStatusColor = rental.status === 'active' ? 'bg-green-500' : 'bg-yellow-500';
  
  return (
    <Card className="border border-primary/20">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Активная аренда</CardTitle>
            <CardDescription>
              Информация о вашей текущей аренде
            </CardDescription>
          </div>
          <Badge className={`${rentalStatusColor} hover:${rentalStatusColor}`}>
            {rental.status === 'active' ? 'Активно' : 'В обработке'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm font-medium">Модель велосипеда</p>
            <p className="text-lg font-semibold">{rental.bikeModel}</p>
          </div>
          <div>
            <p className="text-sm font-medium">Номер</p>
            <p className="text-lg font-semibold">{rental.human_number || '—'}</p>
          </div>
        </div>
        
        <div className="flex justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm font-medium">Начало аренды</p>
            <p className="flex items-center gap-1">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {formatDate(rental.startDate)}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium">Окончание</p>
            <p className="flex items-center gap-1">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {rental.endDate ? formatDate(rental.endDate) : '—'}
            </p>
          </div>
        </div>
        
        {rental.daysLeft !== undefined && (
          <div className="bg-primary/5 p-3 rounded-md">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Дней до окончания:</span>
              <Badge variant={rental.daysLeft <= 3 ? 'destructive' : 'secondary'}>
                {rental.daysLeft}
              </Badge>
            </div>
          </div>
        )}
        
        <div className="flex space-x-2 pt-2">
          {rental.documentsUrl && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-1"
              onClick={handleDocumentClick}
            >
              <FileText className="h-4 w-4" />
              <span>Акт</span>
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={openRulesDialog}
          >
            Договор оферты
          </Button>
        </div>
      </CardContent>
      
      {/* Диалоговые окна */}
      <RentalRulesDialog 
        isOpen={isRulesDialogOpen} 
        onClose={() => setIsRulesDialogOpen(false)} 
      />
      <BikeInstructionDialog 
        isOpen={isInstructionsDialogOpen}
        onClose={() => setIsInstructionsDialogOpen(false)}
      />
    </Card>
  );
};
